(function () {
  'use strict';

  const CACHE_PREFIX = 'alunir-search:v5:';
  const ENTITY_CACHE_TTL = 15 * 60 * 1000;
  const VIEW_EVENT = 'alunir_search_results_view';
  const CLICK_EVENT = 'alunir_search_result_click';
  const ZERO_EVENT = 'alunir_search_zero_results';
  const MOONSTONE_SEARCH_TERMS = new Set(['mondstein', 'moonstone', 'pierre de lune']);
  const MOONSTONE_PRIORITY_HANDLES = [
    'halskette-kalea-mondstein',
    'ohrstecker-ann-mondstein',
    'ring-marie-mondstein',
    'armband-liliane-mondstein'
  ];
  const ENTITY_PREFIX_PATTERN = /^(?:alunir\s+)?(stores?|popups?|pop\s+ups?|boutiques?|magasins?|laden|filialen?|ateliers?|workshops?|services?|beratungen?|reparaturen?|erlebnisse?)\s+(.+)$/i;
  const ENTITY_SOURCE_TOKENS = new Set([
    'store', 'stores', 'popup', 'popups', 'boutique', 'boutiques', 'magasin', 'magasins',
    'laden', 'filiale', 'filialen', 'atelier', 'ateliers', 'workshop', 'workshops',
    'service', 'services', 'beratung', 'beratungen', 'reparatur', 'reparaturen', 'erlebnis', 'erlebnisse'
  ]);
  const ENTITY_GENERIC_TOKENS = new Set([
    ...ENTITY_SOURCE_TOKENS,
    'alunir', 'und', 'and', 'et', 'de', 'der', 'die', 'das', 'the', 'in', 'bei',
    'adresse', 'address', 'offnungszeiten', 'opening', 'hours', 'offen', 'ouvert', 'horaires'
  ]);
  let entityIndexPromise = null;

  function pushDataLayer(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  function readSession(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      // Search must continue when storage is unavailable.
    }
  }

  function normalizeSearchValue(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/&/g, ' und ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function germanSpellingVariant(value) {
    return normalizeSearchValue(value)
      .replace(/ae/g, 'a')
      .replace(/oe/g, 'o')
      .replace(/ue/g, 'u');
  }

  function isRelevantFallbackTitle(searchTerm, title) {
    const query = normalizeSearchValue(searchTerm);
    const candidate = normalizeSearchValue(title);
    if (!query || !candidate) return false;
    if (query.length >= 3 && (candidate.includes(query) || query.includes(candidate))) return true;

    const queryTokens = query.split(' ').filter((token) => token.length >= 3 && !ENTITY_GENERIC_TOKENS.has(token));
    const candidateTokens = candidate.split(' ').filter((token) => token.length >= 3);
    if (!queryTokens.length || !candidateTokens.length) return false;

    return queryTokens.some((queryToken) => candidateTokens.some((candidateToken) => {
      if (candidateToken.includes(queryToken) || queryToken.includes(candidateToken)) return true;
      return queryToken.length >= 5 && candidateToken.length >= 5 && editDistance(queryToken, candidateToken) <= 1;
    }));
  }

  function productHandle(card) {
    const link = card ? card.querySelector('a[href*="/products/"]') : null;
    if (!link) return '';
    try {
      return new URL(link.href, window.location.href).pathname.split('/products/')[1].split('/')[0];
    } catch (error) {
      return '';
    }
  }

  function isUnavailableCard(card) {
    if (!card) return false;
    return Boolean(card.querySelector(
      '.al-product-badge--sold-out, .grid-product__tag--sold-out, .grid-product__tag--soldout, [class*="sold-out"], [class*="soldout"]'
    ));
  }

  function prioritizeFullSearchCards(page, searchTerm) {
    if (!page) return;
    const selectedSort = new URLSearchParams(window.location.search).get('sort_by') || 'relevance';
    if (selectedSort !== 'relevance') return;
    const grid = page.querySelector('[data-search-group="main"] .collection-grid__wrapper > .grid');
    if (!grid) return;

    const cards = Array.from(grid.children).filter((item) => item.matches('.grid-product'));
    if (cards.length < 2) return;
    const normalizedTerm = normalizeSearchValue(searchTerm);
    const priorityHandles = MOONSTONE_SEARCH_TERMS.has(normalizedTerm) ? MOONSTONE_PRIORITY_HANDLES : [];
    const originalPositions = new Map(cards.map((card, index) => [card, index]));

    cards.sort((left, right) => {
      const availabilityDifference = Number(isUnavailableCard(left)) - Number(isUnavailableCard(right));
      if (availabilityDifference !== 0) return availabilityDifference;

      if (priorityHandles.length) {
        const leftPriority = priorityHandles.indexOf(productHandle(left));
        const rightPriority = priorityHandles.indexOf(productHandle(right));
        const normalizedLeftPriority = leftPriority === -1 ? priorityHandles.length : leftPriority;
        const normalizedRightPriority = rightPriority === -1 ? priorityHandles.length : rightPriority;
        if (normalizedLeftPriority !== normalizedRightPriority) return normalizedLeftPriority - normalizedRightPriority;
      }

      return originalPositions.get(left) - originalPositions.get(right);
    });

    cards.forEach((card) => grid.appendChild(card));
  }

  function entityAliasVariants(value) {
    return uniqueValues([normalizeSearchValue(value), germanSpellingVariant(value)]);
  }

  function editDistance(left, right) {
    if (left === right) return 0;
    if (!left.length) return right.length;
    if (!right.length) return left.length;

    const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let leftIndex = 1; leftIndex <= left.length; leftIndex += 1) {
      const current = [leftIndex];
      for (let rightIndex = 1; rightIndex <= right.length; rightIndex += 1) {
        const substitution = previous[rightIndex - 1] + (left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1);
        current[rightIndex] = Math.min(previous[rightIndex] + 1, current[rightIndex - 1] + 1, substitution);
      }
      previous.splice(0, previous.length, ...current);
    }
    return previous[right.length];
  }

  function uniqueValues(values) {
    return Array.from(new Set(values.filter(Boolean)));
  }

  function findEntitySourceUrls() {
    const urls = Array.from(document.querySelectorAll('a[href]')).reduce((matches, anchor) => {
      if (anchor.closest('[data-alunir-search-results]')) return matches;
      const labelTokens = normalizeSearchValue(anchor.textContent).split(' ').filter(Boolean);
      if (!labelTokens.some((token) => ENTITY_SOURCE_TOKENS.has(token))) return matches;

      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin || !url.pathname.includes('/pages/')) return matches;
        matches.push(url.pathname + url.search);
      } catch (error) {
        // Ignore malformed navigation links and continue with the remaining sources.
      }

      return matches;
    }, []);

    return uniqueValues(urls);
  }

  function entityKind(prefix) {
    const normalizedPrefix = normalizeSearchValue(prefix).replace(/\s+/g, '');
    return /^(store|stores|popup|popups|boutique|boutiques|magasin|magasins|laden|filiale|filialen)$/.test(normalizedPrefix)
      ? 'location'
      : 'service';
  }

  function parseEntitySource(html, sourceUrl) {
    const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(sourceDocument.querySelectorAll('h1, h2, h3, h4')).reduce((entities, heading) => {
      const title = heading.textContent.trim();
      const match = normalizeSearchValue(title).match(ENTITY_PREFIX_PATTERN);
      if (!match) return entities;

      const locationOrService = match[2].trim();
      const meaningfulAliasTokens = locationOrService.split(' ')
        .filter((token) => token.length > 1 && !ENTITY_GENERIC_TOKENS.has(token));
      if (!meaningfulAliasTokens.length) return entities;

      const block = heading.closest('.custom__item, [data-block-id], article, section, li') || heading.parentElement;
      const blockText = block ? (block.innerText || block.textContent || '') : (heading.innerText || heading.textContent || '');
      const lines = blockText
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const postalCity = lines.find((line) => /^\d{4}\s+\S+/.test(line));
      const description = lines.filter((line) => normalizeSearchValue(line) !== normalizeSearchValue(title)).slice(0, 4).join('\n');
      const image = block ? block.querySelector('img[src]') : null;
      const aliases = [title, locationOrService];
      if (postalCity) aliases.push(postalCity.replace(/^\d{4}\s+/, ''));

      const fragment = 'alunir-entity-' + normalizeSearchValue(title).replace(/\s+/g, '-');

      entities.push({
        id: sourceUrl + '#' + fragment,
        type: entityKind(match[1]),
        title: title,
        description: description,
        url: sourceUrl + '#' + fragment,
        image: image ? image.currentSrc || image.src : '',
        aliases: uniqueValues(aliases.flatMap(entityAliasVariants))
      });
      return entities;
    }, []);
  }

  function loadEntityIndex() {
    if (entityIndexPromise) return entityIndexPromise;
    const sourceUrls = findEntitySourceUrls();
    if (!sourceUrls.length) return Promise.resolve([]);

    const cacheKey = CACHE_PREFIX + language() + ':entity-index:' + sourceUrls.join('|');
    const cached = readSession(cacheKey);
    if (cached) {
      try {
        const parsedCache = JSON.parse(cached);
        if (parsedCache && Array.isArray(parsedCache.entities) && Date.now() - parsedCache.savedAt < ENTITY_CACHE_TTL) {
          return Promise.resolve(parsedCache.entities);
        }
      } catch (error) {
        // Rebuild an outdated or incomplete cache entry.
      }
    }

    entityIndexPromise = Promise.all(sourceUrls.map((sourceUrl) => fetch(sourceUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    }).then((response) => {
      if (!response.ok) throw new Error('Entity source request failed');
      return response.text();
    }).then((html) => parseEntitySource(html, sourceUrl)).catch(() => [])))
      .then((groups) => groups.flat())
      .then((entities) => {
        const uniqueEntities = Array.from(new Map(entities.map((entity) => [entity.id, entity])).values());
        writeSession(cacheKey, JSON.stringify({ savedAt: Date.now(), entities: uniqueEntities }));
        return uniqueEntities;
      });

    return entityIndexPromise;
  }

  function entityMatchScore(searchTerm, entity) {
    const normalizedQuery = normalizeSearchValue(searchTerm);
    const queryVariants = entityAliasVariants(searchTerm);
    const queryTokens = normalizedQuery.split(' ')
      .filter((token) => token.length > 1 && !ENTITY_GENERIC_TOKENS.has(token));
    if (!queryTokens.length) return 0;

    return entity.aliases.reduce((bestScore, alias) => {
      const aliasTokens = alias.split(' ')
        .filter((token) => token.length > 1 && !ENTITY_GENERIC_TOKENS.has(token));
      if (!aliasTokens.length) return bestScore;
      if (queryVariants.includes(alias) || queryTokens.join(' ') === aliasTokens.join(' ')) return Math.max(bestScore, 100);
      if (queryTokens.every((token) => aliasTokens.includes(token))) return Math.max(bestScore, 86);
      if (aliasTokens.every((token) => queryTokens.includes(token))) return Math.max(bestScore, 82);
      if (queryTokens.length === 1 && aliasTokens.length === 1 && queryTokens[0].length >= 5 && aliasTokens[0].length >= 5) {
        const distance = editDistance(germanSpellingVariant(queryTokens[0]), germanSpellingVariant(aliasTokens[0]));
        const allowedDistance = Math.min(queryTokens[0].length, aliasTokens[0].length) >= 6 ? 2 : 1;
        if (distance <= allowedDistance) return Math.max(bestScore, 84 - distance);
      }
      return bestScore;
    }, 0);
  }

  function resolveNavigationEntity(searchTerm) {
    return loadEntityIndex().then((entities) => {
      const matches = entities
        .map((entity) => ({ ...entity, score: entityMatchScore(searchTerm, entity) }))
        .filter((entity) => entity.score >= 82)
        .sort((left, right) => right.score - left.score);
      if (!matches.length) return null;
      if (matches[0].score < 86 && matches[1] && matches[0].score - matches[1].score < 2) return null;
      return matches[0];
    });
  }

  function decorateEntityHeadings() {
    Array.from(document.querySelectorAll('h1, h2, h3, h4')).forEach((heading) => {
      const title = heading.textContent.trim();
      if (!normalizeSearchValue(title).match(ENTITY_PREFIX_PATTERN)) return;
      if (!heading.id) heading.id = 'alunir-entity-' + normalizeSearchValue(title).replace(/\s+/g, '-');
    });

    if (window.location.hash.indexOf('#alunir-entity-') !== 0) return;
    const target = document.getElementById(window.location.hash.slice(1));
    if (target) window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
  }

  function resetClosedSearchUi() {
    if (window.history.state && window.history.state.alunirSearchOpen) return;
    document.documentElement.classList.remove('alunir-search-open', 'js-drawer-open--search');
    document.body.classList.remove('predictive-overflow-hidden');
    document.querySelectorAll('predictive-search').forEach((root) => {
      root.classList.remove('is-active');
      root.removeAttribute('role');
      root.removeAttribute('aria-label');
      root.removeAttribute('aria-modal');
    });
  }

  function entityLabels(root, type) {
    const isFrench = language().toLowerCase().startsWith('fr');
    if (type === 'location') {
      return {
        kicker: root ? root.getAttribute('data-location-kicker') || (isFrench ? 'Boutique & expérience' : 'Store & Erlebnis') : (isFrench ? 'Boutique & expérience' : 'Store & Erlebnis'),
        cta: root ? root.getAttribute('data-location-cta') || (isFrench ? 'Voir les informations de la boutique' : 'Store-Informationen ansehen') : (isFrench ? 'Voir les informations de la boutique' : 'Store-Informationen ansehen')
      };
    }
    return {
      kicker: root ? root.getAttribute('data-service-kicker') || 'Service' : 'Service',
      cta: root ? root.getAttribute('data-service-cta') || (isFrench ? 'En savoir plus' : 'Mehr erfahren') : (isFrench ? 'En savoir plus' : 'Mehr erfahren')
    };
  }

  function offerLabels(root) {
    return {
      heading: root ? root.getAttribute('data-matching-offers-label') || '' : '',
      cta: root ? root.getAttribute('data-view-all-offers-label') || '' : ''
    };
  }

  function createEntityCard(entity, root) {
    const labels = entityLabels(root, entity.type);
    const card = document.createElement('a');
    const kicker = document.createElement('span');
    const content = document.createElement('span');
    const title = document.createElement('span');
    const description = document.createElement('span');
    const link = document.createElement('span');

    card.className = 'al-search-feature-card al-search-collection-card al-search-entity-card';
    card.href = entity.url;
    card.setAttribute('data-alunir-search-result', '');
    card.setAttribute('data-result-type', entity.type);
    card.setAttribute('data-result-id', entity.id);
    card.setAttribute('data-result-confidence', String(entity.score || 100));
    kicker.className = 'al-search-kicker';
    kicker.textContent = labels.kicker;
    content.className = 'al-search-collection-card__content';
    title.className = 'al-search-collection-card__title';
    title.textContent = entity.title;
    description.className = 'al-search-collection-card__description al-search-entity-card__description';
    description.textContent = entity.description;
    link.className = 'al-search-collection-card__link';
    link.textContent = labels.cta + ' →';
    content.appendChild(title);
    if (entity.description) content.appendChild(description);
    content.appendChild(link);
    card.append(kicker, content);
    return card;
  }

  function applyEntityToResults(container, entity, root) {
    const panel = container ? container.querySelector('[data-alunir-search-results]') : null;
    if (!panel || !entity) return false;

    let layout = panel.querySelector('.al-predictive-search__layout');
    if (!layout) {
      layout = document.createElement('div');
      layout.className = 'al-predictive-search__layout al-predictive-search__layout--feature-only';
      const cta = panel.querySelector('.al-predictive-search__cta');
      const empty = panel.querySelector('.al-predictive-search__empty');
      if (empty) empty.remove();
      panel.insertBefore(layout, cta || null);
    }

    layout.classList.remove('al-predictive-search__layout--products-only');
    let discovery = layout.querySelector('.al-predictive-search__discovery');
    if (!discovery) {
      discovery = document.createElement('aside');
      discovery.className = 'al-predictive-search__discovery';
      layout.prepend(discovery);
    }
    discovery.setAttribute('data-search-group', entity.type);
    discovery.replaceChildren(createEntityCard(entity, root));
    panel.classList.remove('predictive-search-results--none');
    panel.setAttribute('data-has-feature-result', 'true');

    const labels = offerLabels();
    const mainHeading = panel.querySelector('[data-search-group="main"] .al-predictive-search__heading');
    if (mainHeading) mainHeading.textContent = labels.heading;
    const mobileCta = panel.querySelector('.al-predictive-search__cta');
    if (mobileCta && mobileCta.firstChild) mobileCta.firstChild.textContent = labels.cta + ' ';
    return true;
  }

  function language() {
    return document.documentElement.lang || 'de';
  }

  function resultIdentifier(result) {
    return result ? result.getAttribute('data-result-id') || '' : '';
  }

  class AlunirSearchController {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      this.results = root.querySelector('#predictive-search');
      this.input = root.querySelector('input[type="search"]');
      this.context = root.getAttribute('data-context') || 'predictive';
      this.isInline = Boolean(root.closest('.search-content'));
      this.surface = this.isInline ? 'search_page_input' : 'predictive';
      this.abortController = null;
      this.lastViewKey = '';
      this.isOpen = false;
      this.historyActive = false;
      this.returnFocus = null;
      this.inertedElements = [];
      this.popularMarkup = this.results ? this.results.innerHTML : '';

      const productTypeInput = this.form ? this.form.querySelector('input[name="type"]') : null;
      if (productTypeInput) productTypeInput.value = 'product';

      const closeButton = root.querySelector('.btn--close-search');
      if (closeButton && !closeButton.querySelector('.al-search-close-label')) {
        const closeLabel = document.createElement('span');
        closeLabel.className = 'al-search-close-label';
        closeLabel.textContent = root.getAttribute('data-close-label') || '';
        closeButton.type = 'button';
        closeButton.setAttribute('aria-label', closeLabel.textContent);
        closeButton.appendChild(closeLabel);
      }

      if (!this.popularMarkup && this.results) {
        const popularSource = Array.from(document.querySelectorAll('[data-alunir-search-popular]'))
          .find((item) => !root.contains(item));
        if (popularSource) this.popularMarkup = popularSource.outerHTML;
      }

      this.onResultClick = this.onResultClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      root.addEventListener('click', this.onResultClick);
      root.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);
      if (this.input) {
        this.input.addEventListener('input', () => {
          const nextTerm = this.input.value.trim();
          if (!nextTerm.length) {
            this.showPopular();
            return;
          }

          const currentPanel = this.results ? this.results.querySelector('[data-alunir-search-results]') : null;
          const currentTerm = currentPanel ? currentPanel.getAttribute('data-search-query') || '' : '';
          if (currentTerm && normalizeSearchValue(currentTerm) !== normalizeSearchValue(nextTerm)) {
            this.showLoading();
          }
        });
      }
    }

    request(url) {
      const cacheKey = CACHE_PREFIX + language() + ':' + url;
      const cached = readSession(cacheKey);

      if (cached) {
        return Promise.resolve(cached);
      }

      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.showLoading();

      return fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: this.abortController.signal
      }).then((response) => {
        if (!response.ok) {
          throw new Error('Search request failed with status ' + response.status);
        }

        return response.text();
      }).then((html) => {
        writeSession(cacheKey, html);
        return html;
      });
    }

    showLoading() {
      if (!this.results) return;

      this.results.setAttribute('aria-busy', 'true');
      const currentHeight = Math.round(this.results.getBoundingClientRect().height);
      const stableHeight = Math.min(Math.max(currentHeight, 280), Math.round(window.innerHeight * .68));
      this.results.style.setProperty('--al-search-loading-height', stableHeight + 'px');
      this.results.classList.remove('al-search-results--updating');
      this.results.innerHTML = [
        '<div class="al-search-loading" aria-live="polite" aria-busy="true">',
        '<div class="al-search-loading__line"></div>',
        '<div class="al-search-loading__grid">',
        '<div class="al-search-loading__tile"></div>',
        '<div class="al-search-loading__tile"></div>',
        '<div class="al-search-loading__tile"></div>',
        '<div class="al-search-loading__tile"></div>',
        '</div>',
        '</div>'
      ].join('');
    }

    showPopular() {
      if (!this.results || !this.popularMarkup) return false;
      this.results.classList.remove('al-search-results--updating');
      this.results.innerHTML = this.popularMarkup;
      this.decorate('', 'popular');
      return true;
    }

    showFallback(searchTerm) {
      if (!this.results) return;

      this.results.classList.remove('al-search-results--updating');

      const wrapper = document.createElement('div');
      const title = document.createElement('p');
      const link = document.createElement('a');
      const fallbackLabel = this.root.getAttribute('data-fallback-label') || 'Die Suche ist gerade nicht verfügbar.';
      const allResultsLabel = this.root.getAttribute('data-all-results-label') || 'Alle Ergebnisse ansehen';

      wrapper.className = 'al-predictive-search__empty';
      title.className = 'al-predictive-search__empty-title';
      title.textContent = fallbackLabel;
      link.className = 'al-search-text-link';
      link.href = (window.theme && window.theme.routes ? window.theme.routes.search : '/search') + '?type=product&q=' + encodeURIComponent(searchTerm);
      link.textContent = allResultsLabel;
      wrapper.appendChild(title);
      wrapper.appendChild(link);
      this.results.replaceChildren(wrapper);
    }

    hydrateEmptyFromSearchPage(searchTerm) {
      if (!this.results || !searchTerm) return Promise.resolve(false);
      const currentResults = this.results.querySelector('[data-alunir-search-results]');
      const existingFeatureCard = currentResults ? currentResults.querySelector('.al-search-feature-card') : null;
      const existingFeatureGroup = existingFeatureCard && existingFeatureCard.closest('[data-search-group]')
        ? existingFeatureCard.closest('[data-search-group]').getAttribute('data-search-group')
        : 'collection';
      const primaryResultCount = Number(currentResults ? (
        currentResults.getAttribute('data-primary-result-count') ||
        currentResults.getAttribute('data-search-result-count') || 0
      ) : 0);
      if (!currentResults || primaryResultCount > 0) {
        return Promise.resolve(false);
      }

      const searchRoute = window.theme && window.theme.routes ? window.theme.routes.search : '/search';
      const url = searchRoute + '?type=product&q=' + encodeURIComponent(searchTerm) + '&section_id=main-search';
      const cacheKey = CACHE_PREFIX + language() + ':full-fallback:' + url;
      const cached = readSession(cacheKey);
      const request = cached ? Promise.resolve(cached) : fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      }).then((response) => {
        if (!response.ok) throw new Error('Full search fallback failed');
        return response.text();
      }).then((html) => {
        writeSession(cacheKey, html);
        return html;
      });

      return request.then((html) => {
        const responseDocument = new DOMParser().parseFromString(html, 'text/html');
        const searchPage = responseDocument.querySelector('[data-alunir-search-page]');
        const resultCount = Number(searchPage ? searchPage.getAttribute('data-search-result-count') || 0 : 0);
        const groupName = searchPage ? searchPage.getAttribute('data-visible-group') || 'main' : 'main';
        const sourceGroup = searchPage ? searchPage.querySelector('[data-search-group="' + groupName + '"]') : null;
        const sourceCards = sourceGroup ? Array.from(sourceGroup.querySelectorAll('.grid-product'))
          .filter((sourceCard) => {
            const title = sourceCard.querySelector('.grid-product__title');
            return title && isRelevantFallbackTitle(searchTerm, title.textContent);
          })
          .slice(0, 4) : [];
        if (!resultCount || !sourceCards.length) return false;

        const labels = {
          kicker: this.root.getAttribute('data-results-kicker') || '',
          main: this.root.getAttribute('data-jewelry-label') || '',
          component: this.root.getAttribute('data-components-label') || '',
          utility: this.root.getAttribute('data-matching-products-label') || '',
          viewAll: this.root.getAttribute('data-view-all-label') || '',
          cta: this.root.getAttribute('data-view-all-main') || ''
        };
        labels.cta = this.root.getAttribute('data-view-all-' + groupName) || labels.cta;

        const panel = document.createElement('div');
        const layout = document.createElement('div');
        const main = document.createElement('div');
        const section = document.createElement('section');
        const header = document.createElement('div');
        const headingWrap = document.createElement('div');
        const kicker = document.createElement('div');
        const heading = document.createElement('h2');
        const allLink = document.createElement('a');
        const grid = document.createElement('div');
        const cta = document.createElement('a');

        panel.className = 'al-predictive-search';
        panel.id = 'predictive-search-results';
        panel.setAttribute('data-alunir-search-results', '');
        panel.setAttribute('data-search-query', searchTerm);
        panel.setAttribute('data-search-result-count', String(resultCount));
        panel.setAttribute('data-primary-result-count', String(sourceCards.length));
        panel.setAttribute('data-has-feature-result', existingFeatureCard ? 'true' : 'false');
        layout.className = 'al-predictive-search__layout' + (existingFeatureCard ? '' : ' al-predictive-search__layout--products-only');
        main.className = 'al-predictive-search__main';
        section.setAttribute('data-search-group', groupName);
        header.className = 'al-predictive-search__header';
        kicker.className = 'al-search-kicker';
        kicker.textContent = labels.kicker;
        heading.className = 'al-predictive-search__heading';
        heading.textContent = labels[groupName] || labels.main;
        allLink.className = 'al-search-text-link';
        allLink.href = searchRoute + '?type=product&q=' + encodeURIComponent(searchTerm);
        allLink.textContent = labels.viewAll + ' →';
        grid.className = 'al-search-result-grid';

        sourceCards.forEach((sourceCard) => {
          const wrapper = document.createElement('div');
          wrapper.className = 'al-search-result-card';
          wrapper.setAttribute('data-alunir-search-result', '');
          wrapper.setAttribute('data-result-type', 'product');
          wrapper.setAttribute('data-result-id', sourceCard.getAttribute('data-product-id') || '');
          wrapper.appendChild(sourceCard.cloneNode(true));
          grid.appendChild(wrapper);
        });

        cta.className = 'al-predictive-search__cta';
        cta.href = allLink.href;
        cta.textContent = labels.cta + ' →';
        headingWrap.append(kicker, heading);
        header.append(headingWrap, allLink);
        section.append(header, grid);
        main.appendChild(section);
        if (existingFeatureCard) {
          const discovery = document.createElement('aside');
          discovery.className = 'al-predictive-search__discovery';
          discovery.setAttribute('data-search-group', existingFeatureGroup || 'collection');
          discovery.appendChild(existingFeatureCard.cloneNode(true));
          layout.appendChild(discovery);
        }
        layout.appendChild(main);
        panel.append(layout, cta);
        this.results.replaceChildren(panel);
        this.stabilizeResultCards();
        return true;
      }).catch(() => false);
    }

    enrichWithEntity(searchTerm) {
      if (!this.results || !searchTerm) return Promise.resolve(false);
      return resolveNavigationEntity(searchTerm)
        .then((entity) => applyEntityToResults(this.results, entity, this.root))
        .catch(() => false);
    }

    decorate(searchTerm, group) {
      if (!this.results) return;

      this.results.classList.remove('al-search-results--updating');
      this.results.setAttribute('aria-busy', 'false');
      this.stabilizeResultCards();
      this.labelMixedOfferResults();
      this.results.querySelectorAll('[data-alunir-search-result]').forEach((result) => {
        result.setAttribute('data-search-term', searchTerm);
      });

      const resultRoot = this.results.querySelector('[data-alunir-search-results]');
      const resultCount = resultRoot ? Number(resultRoot.getAttribute('data-search-result-count') || 0) : 0;
      const hasFeatureResult = Boolean(resultRoot && (
        resultRoot.getAttribute('data-has-feature-result') === 'true' ||
        resultRoot.querySelector('.al-search-feature-card')
      ));
      const visibleGroups = Array.from(this.results.querySelectorAll('[data-search-group]'))
        .filter((item) => item.querySelector('[data-alunir-search-result]'))
        .map((item) => item.getAttribute('data-search-group'));
      const visibleGroup = group || visibleGroups.join(',') || 'none';
      const viewKey = [searchTerm.toLowerCase(), this.context, visibleGroup, resultCount].join('|');

      if (viewKey !== this.lastViewKey) {
        this.lastViewKey = viewKey;
        pushDataLayer({
          event: VIEW_EVENT,
          search_term: searchTerm,
          language: language(),
          surface: this.surface,
          visible_group: visibleGroup,
          position: null,
          result_count: resultCount
        });

        if (searchTerm && resultCount === 0 && !hasFeatureResult) {
          pushDataLayer({
            event: ZERO_EVENT,
            search_term: searchTerm,
            language: language(),
            surface: this.surface,
            visible_group: 'none',
            position: null,
            result_count: 0
          });
        }
      }
    }

    stabilizeResultCards() {
      if (!this.results) return;

      this.results.querySelectorAll(
        '.al-search-result-card [data-aos], .al-search-result-card .aos-init, ' +
        '.al-search-secondary__result [data-aos], .al-search-secondary__result .aos-init'
      ).forEach((item) => {
        item.removeAttribute('data-aos');
        item.removeAttribute('data-aos-delay');
        item.removeAttribute('data-aos-duration');
        item.classList.remove('aos-init', 'aos-animate');
      });
    }

    labelMixedOfferResults() {
      if (!this.results) return;

      const mainGroup = this.results.querySelector('[data-search-group="main"]');
      if (!mainGroup) return;
      const containsExperience = Array.from(mainGroup.querySelectorAll('.grid-product__title')).some((title) => {
        const value = normalizeSearchValue(title.textContent);
        return /(?:terminbuchung|booking|appointment|workshop|atelier|service|erlebnis|schmuck selber herstellen)/.test(value);
      });
      if (!containsExperience) return;

      const labels = offerLabels(this.root);
      const heading = mainGroup.querySelector('.al-predictive-search__heading');
      if (heading) heading.textContent = labels.heading;
      const mobileCta = this.results.querySelector('.al-predictive-search__cta');
      if (mobileCta && mobileCta.firstChild) mobileCta.firstChild.textContent = labels.cta + ' ';
    }

    onOpen() {
      if (this.isOpen) return;
      this.isOpen = true;
      if (this.input) this.input.setAttribute('aria-expanded', 'true');

      if (!this.isInline) {
        document.documentElement.classList.add('alunir-search-open');
        this.root.setAttribute('role', 'dialog');
        this.root.setAttribute('aria-label', this.input ? (this.input.getAttribute('aria-label') || this.input.getAttribute('placeholder') || this.root.getAttribute('data-dialog-label') || 'Search') : (this.root.getAttribute('data-dialog-label') || 'Search'));
        this.returnFocus = document.activeElement && !this.root.contains(document.activeElement)
          ? document.activeElement
          : document.querySelector('.js-search-header');

        if (window.matchMedia('(max-width: 768px)').matches) {
          this.root.setAttribute('aria-modal', 'true');
          this.setBackgroundInert(true);
        }
      }

      if (
        !this.isInline &&
        window.matchMedia('(max-width: 768px)').matches &&
        !this.historyActive &&
        !(window.history.state && window.history.state.alunirSearchOpen)
      ) {
        window.history.pushState({ alunirSearchOpen: true }, '', window.location.href);
        this.historyActive = true;
      }
    }

    onClose() {
      if (!this.isOpen) return;
      this.isOpen = false;
      if (this.input) this.input.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('alunir-search-open');
      this.root.removeAttribute('role');
      this.root.removeAttribute('aria-label');
      this.root.removeAttribute('aria-modal');
      this.setBackgroundInert(false);

      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }

      if (this.historyActive && window.history.state && window.history.state.alunirSearchOpen) {
        this.historyActive = false;
        window.history.back();
      } else {
        this.historyActive = false;
      }

      const fallbackTrigger = Array.from(this.root.querySelectorAll('.btn--search, .al-header-search-compact__button'))
        .find((item) => item.getClientRects().length);
      const focusTarget = this.returnFocus && this.returnFocus.isConnected && this.returnFocus.getClientRects().length
        ? this.returnFocus
        : fallbackTrigger;
      this.returnFocus = null;
      window.setTimeout(() => {
        this.root.classList.remove('is-active');
        document.documentElement.classList.remove('js-drawer-open--search');
        document.body.classList.remove('predictive-overflow-hidden');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
      }, 0);
      window.setTimeout(() => {
        if (this.isOpen) return;
        this.root.classList.remove('is-active');
        document.documentElement.classList.remove('js-drawer-open--search');
        document.body.classList.remove('predictive-overflow-hidden');
      }, 300);
    }

    setBackgroundInert(active) {
      if (!active) {
        this.inertedElements.forEach((entry) => {
          if (!entry.element.isConnected) return;
          if (entry.hadInert) entry.element.setAttribute('inert', '');
          else entry.element.removeAttribute('inert');
          if (entry.ariaHidden === null) entry.element.removeAttribute('aria-hidden');
          else entry.element.setAttribute('aria-hidden', entry.ariaHidden);
        });
        this.inertedElements = [];
        return;
      }

      if (this.inertedElements.length) return;
      let current = this.root;
      while (current && current.parentElement && current.parentElement !== document.documentElement) {
        Array.from(current.parentElement.children).forEach((sibling) => {
          if (sibling === current || sibling.matches('script, style, link')) return;
          if (this.inertedElements.some((entry) => entry.element === sibling)) return;
          this.inertedElements.push({
            element: sibling,
            hadInert: sibling.hasAttribute('inert'),
            ariaHidden: sibling.getAttribute('aria-hidden')
          });
          sibling.setAttribute('inert', '');
          sibling.setAttribute('aria-hidden', 'true');
        });
        current = current.parentElement;
        if (current === document.body) break;
      }
    }

    onPopState() {
      if (!this.isOpen) return;
      this.historyActive = false;
      document.dispatchEvent(new CustomEvent('predictive-search:close'));
    }

    onKeydown(event) {
      if (!this.isOpen || event.key !== 'Tab' || !window.matchMedia('(max-width: 768px)').matches) return;

      const focusable = Array.from(this.root.querySelectorAll(
        'input:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter((item) => item.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onResultClick(event) {
      if (!event.target.closest('a[href]')) return;
      const result = event.target.closest('[data-alunir-search-result]');
      if (!result || !this.root.contains(result)) return;

      const group = result.closest('[data-search-group]');
      const groupName = group ? group.getAttribute('data-search-group') : 'main';
      const groupResults = group ? Array.from(group.querySelectorAll('[data-alunir-search-result]')) : [result];
      const type = result.getAttribute('data-result-type') || 'product';
      const payload = {
        event: CLICK_EVENT,
        search_term: result.getAttribute('data-search-term') || (this.input ? this.input.value.trim() : ''),
        language: language(),
        surface: this.surface,
        visible_group: groupName,
        position: groupResults.indexOf(result) + 1,
        result_count: groupResults.length
      };

      if (type === 'collection') {
        payload.collection_id = resultIdentifier(result);
      } else if (type === 'product') {
        payload.product_id = resultIdentifier(result);
      } else {
        payload.resource_type = type;
        payload.resource_id = resultIdentifier(result);
      }

      pushDataLayer(payload);
    }
  }

  function initFullSearchPage() {
    const page = document.querySelector('[data-alunir-search-page]');
    if (!page) return;

    const searchTerm = page.getAttribute('data-search-term') || '';
    const resultCount = Number(page.getAttribute('data-search-result-count') || 0);
    const editorial = page.querySelector('[data-alunir-search-editorial]');
    const predictiveUrl = page.getAttribute('data-predictive-url');
    const visibleGroup = page.getAttribute('data-visible-group') || 'main';
    prioritizeFullSearchCards(page, searchTerm);
    document.addEventListener('collection:reloaded', () => {
      window.requestAnimationFrame(() => prioritizeFullSearchCards(page, searchTerm));
    });
    let zeroReported = false;
    const reportZero = () => {
      if (zeroReported || !searchTerm || resultCount !== 0) return;
      zeroReported = true;
      pushDataLayer({
        event: ZERO_EVENT,
        search_term: searchTerm,
        language: language(),
        surface: 'search_page',
        visible_group: 'none',
        position: null,
        result_count: 0
      });
    };

    pushDataLayer({
      event: VIEW_EVENT,
      search_term: searchTerm,
      language: language(),
      surface: 'search_page',
      visible_group: visibleGroup,
      position: null,
      result_count: resultCount
    });

    page.addEventListener('click', (event) => {
      if (!event.target.closest('a[href]')) return;
      const result = event.target.closest('[data-alunir-search-result]') || event.target.closest('.grid-product');
      if (!result || !page.contains(result)) return;

      const group = result.closest('[data-search-group]');
      const groupName = group ? group.getAttribute('data-search-group') : visibleGroup;
      let groupResults = [result];
      if (group) {
        groupResults = Array.from(group.querySelectorAll('[data-alunir-search-result]'));
        if (!groupResults.length) groupResults = Array.from(group.querySelectorAll('.grid-product'));
      }
      const type = result.getAttribute('data-result-type') || 'product';
      const id = result.getAttribute('data-result-id') || result.getAttribute('data-product-id') || '';
      const payload = {
        event: CLICK_EVENT,
        search_term: searchTerm,
        language: language(),
        surface: 'search_page',
        visible_group: groupName,
        position: groupResults.indexOf(result) + 1,
        result_count: resultCount
      };

      if (type === 'collection') payload.collection_id = id;
      else if (type === 'product') payload.product_id = id;
      else {
        payload.resource_type = type;
        payload.resource_id = id;
      }
      pushDataLayer(payload);
    });

    if (!editorial || !predictiveUrl || !searchTerm) {
      reportZero();
      return;
    }
    const cacheKey = CACHE_PREFIX + language() + ':editorial:' + predictiveUrl;
    const cached = readSession(cacheKey);
    const request = cached ? Promise.resolve(cached) : fetch(predictiveUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    }).then((response) => {
      if (!response.ok) throw new Error('Editorial search request failed');
      return response.text();
    }).then((html) => {
      writeSession(cacheKey, html);
      return html;
    });

    const pageSearchRoot = page.querySelector('predictive-search');
    resolveNavigationEntity(searchTerm).then((entity) => {
      if (entity) {
        editorial.replaceChildren(createEntityCard(entity, pageSearchRoot));
        editorial.hidden = false;
        return;
      }

      return request.then((html) => {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const featureCard = documentFragment.querySelector('.al-search-feature-card');
        if (!featureCard) {
          reportZero();
          return;
        }
        editorial.replaceChildren(featureCard.cloneNode(true));
        editorial.hidden = false;
      });
    }).catch(() => {
      reportZero();
      // The Liquid search page remains complete when editorial enrichment fails.
    });
  }

  window.AlunirSearchController = AlunirSearchController;

  window.addEventListener('pageshow', () => window.setTimeout(resetClosedSearchUi, 0));
  window.addEventListener('popstate', () => window.setTimeout(resetClosedSearchUi, 350));

  function initAlunirSearch() {
    decorateEntityHeadings();
    initFullSearchPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlunirSearch);
  } else {
    initAlunirSearch();
  }
})();
